import styled from 'styled-components';
import { ComponentRepresentation } from '@ardoq/basic-components';
import {
  ComponentItemContainer,
  ComponentItemTopRow,
  ComponentLabel,
  ComponentLabelContainer,
  IconContainer,
  OtherLabelsContainer,
} from './atoms';
import { BodyCellItemModel, ComponentMatrix2Commands } from '../types';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import {
  OtherLabelBadge as OtherLabel,
  UrlFieldValueIcon,
  UrlFieldValueList,
  maybeDispatchUrlIconClick,
} from '@ardoq/graph';
import { ViewIds } from '@ardoq/api-types';
import { GLOBAL_HANDLER_ID_ATTRIBUTE } from 'consts';
import { IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { UnstyledButton } from '@ardoq/button';
import { LABEL_LINE_HEIGHT } from '../consts';
import { truncationPopoverAttrs } from './truncationPopover';
import { useHoveredItemHandlers } from '../useHoveredItemHandlers';

// Top-align the link icon with the first text line so multi-line cells anchor
// it to the top of the block. The top margin centers the icon on the first-line
// box (matching the spec's optical alignment with the text cap height,
// ARD-31427).
const URL_ICON_SIZE = IconSize.SMALL;
const LINK_ICON_TOP_OFFSET = (LABEL_LINE_HEIGHT - URL_ICON_SIZE) / 2;

const LinkIcon = styled(UrlFieldValueIcon)<{ $isDark: boolean }>`
  color: ${({ $isDark }) =>
    $isDark ? colors.iconOnInvertedStrong : colors.iconStrong};
  margin-top: ${LINK_ICON_TOP_OFFSET}px;
`;

type ComponentItemArgs = BodyCellItemModel & {
  isHovered: boolean;
  isSelected: boolean;
  commands: ComponentMatrix2Commands;
};
const ComponentItem = ({
  componentId,
  fillColor,
  hoverFillColor,
  isDark,
  isContext,
  componentLabel,
  representationData,
  urlFieldValues,
  otherLabels,
  otherLabelWrappedLines,
  isHovered,
  isSelected,
  commands,
}: ComponentItemArgs) => {
  const { onMouseEnter, onMouseLeave } = useHoveredItemHandlers(
    componentId,
    commands
  );
  const backgroundColor = isHovered ? hoverFillColor || fillColor : fillColor;
  const urlFieldValuesList = urlFieldValues ?? [];
  return (
    <ComponentItemContainer
      {...{ [COMPONENT_ID_ATTRIBUTE]: componentId }}
      {...{ [GLOBAL_HANDLER_ID_ATTRIBUTE]: componentId }}
      style={{ backgroundColor }}
      role="button"
      aria-label={`Component ${componentLabel}`}
      className="component skipContextUpdate"
      $isContext={isContext}
      $isSelected={isSelected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={e => {
        e.stopPropagation();
        commands.onItemSelect(componentId);
        commands.onComponentClick(componentId);
      }}
      onContextMenu={() => {
        // Right-click selects the item; the context menu itself is opened by
        // the global ContextMenuManager via data-global-handler-id delegation.
        commands.onItemSelect(componentId);
      }}
      onDoubleClick={() => commands.onComponentDoubleClick(componentId)}
    >
      <ComponentItemTopRow>
        <IconContainer $isDark={isDark}>
          <ComponentRepresentation
            isImage={representationData.isImage}
            value={representationData.value}
            icon={representationData.icon}
          />
        </IconContainer>
        <ComponentLabelContainer>
          <ComponentLabel $isDark={isDark} {...truncationPopoverAttrs}>
            {componentLabel}
          </ComponentLabel>
        </ComponentLabelContainer>
        {Boolean(urlFieldValuesList.length) && (
          <WithPopover
            content={() => (
              <UrlFieldValueList
                urlFieldValues={urlFieldValuesList}
                viewId={ViewIds.COMPONENT_MATRIX_2}
              />
            )}
            preferredPlacement={WithPopoverPlacement.TOP}
            acceptsMouse
          >
            <UnstyledButton
              type="button"
              onClick={e => {
                maybeDispatchUrlIconClick(
                  urlFieldValuesList,
                  ViewIds.COMPONENT_MATRIX_2
                );
                e.stopPropagation();
              }}
            >
              <LinkIcon $isDark={isDark} />
            </UnstyledButton>
          </WithPopover>
        )}
      </ComponentItemTopRow>
      {otherLabels.length > 0 && (
        <OtherLabelsContainer>
          {otherLabels.map(([title, caption, showTitle], index) => (
            <OtherLabel
              key={`${title}:${caption}:${String(showTitle)}`}
              title={title}
              caption={caption}
              showTitle={showTitle}
              preset="unshadowed"
              wrappedLines={otherLabelWrappedLines?.[index]}
            />
          ))}
        </OtherLabelsContainer>
      )}
    </ComponentItemContainer>
  );
};

export { ComponentItem };
